import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import {
  Box,
  Container,
  Stack,
  Typography,
  CircularProgress,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Tooltip,
} from '@mui/material'
import { Download, Print as PrintIcon } from '@mui/icons-material'
import {
  publicNorthStarApi,
  PublicNorthStarResponse,
  NorthStarSectionInstance,
} from '../services/api'
import { SECTION_RENDERERS } from '../components/north-star/sections/registry'
import { ApiError } from '../services/api'

// Inline font + theme styles for the premium aesthetic.
// Loaded inline so this page doesn't depend on global theme.
const PUBLIC_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Newsreader:opsz,wght@6..72,400;6..72,500;6..72,600;6..72,700&family=Inter:wght@400;500;600&display=swap');

:root {
  --ns-display-font: "Newsreader", "Times New Roman", serif;
}

body.ns-public {
  background: #fbf9f4;
  color: #1a1a1a;
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
  font-size: 16px;
  line-height: 1.6;
}
body.ns-public .ns-page-root {
  /* overflow-x: clip prevents horizontal bleed from section tints without
     turning this element into a scroll container, so position:sticky on the
     nested AppBar still anchors to the viewport. */
  overflow-x: clip;
}

@media print {
  body[data-print="true"] .ns-no-print { display: none !important; }
  body[data-print="true"] .ns-anchor-nav { display: none !important; }
  body[data-print="true"] .ns-sticky-pdf { display: none !important; }
  body[data-print="true"] { background: white; }
}
`

function NotFound() {
  return (
    <Container maxWidth="sm" sx={{ py: 12, textAlign: 'center' }}>
      <Typography
        sx={{
          fontFamily: 'var(--ns-display-font)',
          fontSize: 32,
          fontWeight: 500,
        }}
      >
        This page isn't available.
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
        The link you opened may have been deactivated or rotated by the MissFit Mindset team.
      </Typography>
      <Button
        component="a"
        href="https://missfitmindset.ai"
        variant="outlined"
        sx={{ mt: 4 }}
      >
        Visit missfitmindset.ai
      </Button>
    </Container>
  )
}

export default function NorthStarPublic() {
  const { slug = '' } = useParams<{ slug: string }>()
  const [searchParams] = useSearchParams()
  const preview = searchParams.get('preview') === '1'
  const [data, setData] = useState<PublicNorthStarResponse | null>(null)
  const [notFound, setNotFound] = useState(false)
  const [loading, setLoading] = useState(true)
  const [downloadingPdf, setDownloadingPdf] = useState(false)
  const pageRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // Inject font + print styles once
    const style = document.createElement('style')
    style.textContent = PUBLIC_CSS
    document.head.appendChild(style)
    document.body.classList.add('ns-public')
    return () => {
      document.body.classList.remove('ns-public')
      document.head.removeChild(style)
    }
  }, [])

  useEffect(() => {
    if (!slug) return
    let cancelled = false
    ;(async () => {
      try {
        setLoading(true)
        setNotFound(false)
        const resp = await publicNorthStarApi.getBySlug(slug, preview)
        if (!cancelled) setData(resp)
      } catch (err) {
        const apiErr = err as ApiError
        if (apiErr?.status === 404) {
          if (!cancelled) setNotFound(true)
        } else {
          console.error(err)
          if (!cancelled) setNotFound(true)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    // Fire-and-forget view log
    if (!preview) {
      publicNorthStarApi.logView(slug)
    }
    return () => {
      cancelled = true
    }
  }, [slug, preview])

  useEffect(() => {
    if (data?.client?.name) {
      document.title = `${data.client.name} — North Star`
    }
  }, [data])

  const enabledSections = useMemo<NorthStarSectionInstance[]>(
    () =>
      (data?.sections || [])
        .filter((s) => s.enabled !== false)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [data]
  )

  const downloadPdf = async () => {
    if (!pageRef.current) return
    setDownloadingPdf(true)
    document.body.setAttribute('data-print', 'true')
    try {
      // Dynamic import keeps the lib out of the initial bundle
      const html2pdf = (await import('html2pdf.js')).default
      const filename = `${(data?.client?.name || 'NorthStar').replace(/\s+/g, '-')}-NorthStar.pdf`
      await html2pdf()
        .from(pageRef.current)
        .set({
          margin: 12,
          filename,
          html2canvas: { scale: 2, useCORS: true, logging: false },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
          pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
        })
        .save()
    } catch (err) {
      console.error('PDF export failed', err)
    } finally {
      document.body.removeAttribute('data-print')
      setDownloadingPdf(false)
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (notFound || !data) {
    return <NotFound />
  }

  return (
    <Box ref={pageRef} className="ns-page-root">
      {preview && (
        <Box
          className="ns-no-print"
          sx={{ background: '#fff3cd', color: '#664d03', py: 1, textAlign: 'center', fontSize: 13 }}
        >
          PREVIEW · Not visible to recruiters until you Publish
        </Box>
      )}

      <AppBar
        className="ns-anchor-nav ns-no-print"
        position="sticky"
        elevation={0}
        sx={{
          background: 'rgba(251, 249, 244, 0.92)',
          backdropFilter: 'blur(8px)',
          color: '#1a1a1a',
          zIndex: (theme) => theme.zIndex.appBar + 1,
          borderBottom: 1,
          borderColor: 'rgba(0,0,0,0.08)',
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 48, sm: 56 } }}>
          <Typography
            sx={{
              fontFamily: 'var(--ns-display-font)',
              fontWeight: 600,
              letterSpacing: '-0.01em',
              fontSize: 16,
              flex: 1,
            }}
          >
            MissFit
          </Typography>
          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 2 }}>
            {enabledSections.slice(1).map((s) => {
              const tpl = data.templates[s.sectionTypeId]
              if (!tpl) return null
              return (
                <a
                  key={s.sectionTypeId}
                  href={`#${s.sectionTypeId}`}
                  style={{
                    color: '#5a5a5a',
                    fontSize: 12,
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                    textDecoration: 'none',
                  }}
                >
                  {tpl.name}
                </a>
              )
            })}
          </Box>
          <Stack direction="row" spacing={1} sx={{ ml: 2 }}>
            <Tooltip title="Print">
              <IconButton size="small" onClick={() => window.print()} aria-label="Print">
                <PrintIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Button
              startIcon={downloadingPdf ? <CircularProgress size={14} /> : <Download />}
              onClick={downloadPdf}
              disabled={downloadingPdf}
              size="small"
              sx={{ color: '#1a1a1a' }}
            >
              PDF
            </Button>
          </Stack>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ pb: 12 }}>
        {enabledSections.map((section, idx) => {
          const template = data.templates[section.sectionTypeId]
          if (!template) return null
          const Renderer = SECTION_RENDERERS[template.renderType]
          if (!Renderer) return null
          const resolvedTitle = section.titleOverride?.trim() || template.name
          const resolvedDescription =
            section.descriptionOverride?.trim() ?? template.description ?? ''
          return (
            <Renderer
              key={section.sectionTypeId}
              template={template}
              values={section.fieldValues || {}}
              title={resolvedTitle}
              description={resolvedDescription}
              sectionNumber={idx === 0 ? undefined : idx}
              clientName={data.client?.name || ''}
              currentRole={data.client?.currentRole || ''}
              publishedAt={data.publishedAt}
              disclaimer={data.meta?.compDisclaimer}
            />
          )
        })}

        <Box className="ns-no-print" sx={{ mt: 8, pt: 4, borderTop: 1, borderColor: 'rgba(0,0,0,0.08)', textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Crafted by MissFit Mindset
            {data.publishedAt && ` · ${new Date(data.publishedAt).toLocaleDateString()}`}
          </Typography>
          <Box sx={{ mt: 1 }}>
            <a
              href="https://missfitmindset.ai"
              style={{ color: '#5a5a5a', fontSize: 12, textDecoration: 'none' }}
            >
              missfitmindset.ai
            </a>
          </Box>
        </Box>
      </Container>
    </Box>
  )
}
